'use client';

import { forwardRef, useContext, useEffect, useState } from "react";
import classes from "./public_provider_list.module.css";
import FilterContext from "@/providers/filter-context";
import ProviderInfoContext from "@/providers/provider-info-context";
import { getPerPageOptions, getSortOptions } from "@/variables/variables";
import DropdownBase from "../common/dropdown/dropdown_base";
import CustomSVG from "../common/svg";
import ChevronDown from '@/assets/svg/icons/chevron-down.svg';
import ChevronUp from '@/assets/svg/icons/chevron-up.svg';
import Check from '@/assets/svg/icons/check.svg';
import AlertCircle from '@/assets/svg/icons/alert-circle.svg';
import List from '@/assets/svg/icons/list.svg';
import Grid from '@/assets/svg/icons/grid.svg';
import { Dropdown } from "react-bootstrap";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import CustomButton from "../common/buttons/button";
import EmptyProviderLogo from '@/assets/png/empty-event-logo.png';
import SafeImage from "@/components/common/safe_image";
import customImageLoader from '../../../imageLoader';

const PUBLIC_PROVIDER_CARD_SIZES = '(max-width: 768px) 100vw, 33vw';
import MapPin from '@/assets/svg/icons/map-pin.svg';
import CustomBadge from "../common/badges/badge";
import AuthContext from "@/providers/auth-context";
import { LikeButton } from "../home_page/providers_mosaic_view/providers_mosaic_view";
import ArrowRight from '@/assets/svg/icons/arrow-right.svg';
import Link from "next/link";
import Filter from '@/assets/svg/icons/filter.svg';

const per_page_options = getPerPageOptions();
const sort_options = getSortOptions();

export default function PublicProviderList({anotherCount}) {
    const filters = useContext(FilterContext);
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const use_params = useParams();
    const providerInfos = useContext(ProviderInfoContext);
    const authCtx = useContext(AuthContext);
    
    const handleSelect = (e, val, type) => {
        e.preventDefault();
        if(type === "per_page") {
            filters.setPerPage(val);
        } else if(type === "order") {
            filters.setOrder(val);
        }

        const params = new URLSearchParams(Array.from(searchParams.entries()));
        params.delete("page");
        if(val) {
            params.set(type, `${val}`);
        } else {
            params.delete(type);
        }
        router.push(`${pathname}?${params.toString()}`, {scroll: false});
    }

    const showMore = () => {
        filters.showMore(use_params["provider_type"], use_params["address_city"]);
    }

    useEffect(() => {
        if(filters.providers && filters.providers.providers && filters.providers.providers.length > 0) {
            const favouriteProviders = filters.providers.providers.filter(p => p.is_favourite);

            authCtx.setTempFavouriteIds(prev => {
                const ids = new Set(prev);
                favouriteProviders.forEach(provider => ids.add(provider.id));
                return Array.from(ids);
            });

            authCtx.setTempFavouriteNotes(prev => {
                const notes = new Map(prev.map(note => [note.id, note]));
                favouriteProviders.forEach(provider => {
                    notes.set(provider.id, {id: provider.id, note: provider.favouriteNote || ""});
                });
                return Array.from(notes.values());
            });
        }
    }, [filters.providers])

    const others_count = filters.pager.count_rows - (filters.pager.page * filters.pager.per_page);
    
    const loadMore = (distance) => {
        filters.setDistance(distance);

        const params = new URLSearchParams(Array.from(searchParams.entries()));
        params.set("distance", distance);
        router.push(`${pathname}?${params.toString()}`, {scroll: false});
    } 

    const setViewType = (val) => {
        providerInfos.setViewType(val);
        localStorage.setItem("public_providers_view_type", val);
    }

    useEffect(() => {
        const temp_viewType = localStorage.getItem("public_providers_view_type");
        if(temp_viewType) providerInfos.setViewType(temp_viewType);
    }, [])

    const activeFilterCount = (filters.getFilterBadges?.().length || 0) + (filters.addressCity ? 1 : 0);
    const hasActiveFilters = activeFilterCount > 0;

    return <div className={`${classes["public-provider-list-main"]}`}>
        <div className={`${classes["public-provider-list-header"]}`}>
            <div className={`${classes["public-provider-list-count"]}`}>
                <p className="margin-0 color-gray-800 text-md-medium">
                    {`${filters.pager.count_rows} találat`}
                </p>
                {hasActiveFilters && (
                    <div className={`${classes["public-provider-filter-indicator"]}`}>
                        <CustomSVG className="stroke-gray-500" SvgFunction={Filter} height={16} width={16} />
                        <CustomBadge size="sm" color={`gray`}>
                            {activeFilterCount}
                        </CustomBadge>
                        <span className="text-xs-regular color-gray-600 text-nowrap">Szűrés aktív</span>
                    </div>
                )}
            </div>
            <div className={`${classes["public-provider-list-inner"]}`}>
                <PublicProviderHeaderDropdown label={"Találat egy oldalon"} type={"per_page"} options={per_page_options} value={filters.perPage} handleSelect={handleSelect} />
                {/*<PublicProviderHeaderDropdown label={"Sorrendezés"} type={"order"} options={sort_options} value={filters.order} handleSelect={handleSelect} />*/}
                <div className="d-flex flex-column">
                    <div className="d-flex text-sm-medium color-gray-700 text-nowrap gap-1">
                        Nézet
                    </div>
                    <div className={`${classes["public-provider-view-switch"]}`}>
                        <button type="button" data-testid="public-provider-view-switch-list" aria-label="Lista nézet" className={`${classes["public-provider-view-switch-item"]} ${providerInfos.viewType === "list" ? classes["active"] : ""}`} onClick={() => setViewType("list")}>
                            <CustomSVG className="stroke-gray-500" SvgFunction={List} height={20} width={20} />
                            <p className="margin-0 color-gray-500 text-sm-semibold">Lista</p>
                        </button>
                        <button type="button" data-testid="public-provider-view-switch-grid" aria-label="Képes nézet" className={`${classes["public-provider-view-switch-item"]} ${providerInfos.viewType === "grid" ? classes["active"] : ""}`} onClick={() => setViewType("grid")}>
                            <CustomSVG className="stroke-gray-500" SvgFunction={Grid} height={20} width={20} />
                            <p className="margin-0 color-gray-500 text-sm-semibold">Képes</p>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        {filters.providers && filters.providers.providers &&
            (filters.providers.providers.length === 0
                ? <NoResultDiv clickEvent={loadMore} filters={filters}  />
                : <>
                    {filters.providers.providers.map((p, index) => <div key={`public-provider-list-card-${p.id}`} className={`${classes["provider-list-card-main"]} ${providerInfos.viewType === "list" ? classes["active"] : ""}`}>
                        <Link className={`w-100 h-100 d-flex flex-row pointer text-decoration-none`} title={p.name} href={`/${p.url}`} rel="noopener noreferrer" target="_blank">
                            <div className={`${classes["provider-list-card-img-container"]} ${(p.images && p.images.length > 0) ? classes["non-empty"] : ""}`}>
                                {p.images && p.images.length > 0
                                    ? <img
                                        src={customImageLoader({ src: p.images[0], width: 640, quality: 75 })}
                                        srcSet={`${customImageLoader({ src: p.images[0], width: 640, quality: 75 })} 640w, ${customImageLoader({ src: p.images[0], width: 1080, quality: 75 })} 1080w`}
                                                                                sizes={PUBLIC_PROVIDER_CARD_SIZES}
                                        alt={p.name}
                                        loading={index === 0 ? 'eager' : 'lazy'}
                                        fetchPriority={index === 0 ? 'high' : undefined}
                                        decoding="async"
                                        style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', inset: 0 }}
                                      />
                                                                        : <SafeImage sizes={PUBLIC_PROVIDER_CARD_SIZES} src={EmptyProviderLogo} alt={"Szolgáltató képe"} />
                                }
                            </div>
                            <div className={`${classes["provider-list-card-text-container"]} overflow-hidden`}>
                                <div className="d-flex flex-column align-self-stretch w-100 text-truncate gap-1 flex-shrink-0">
                                    <p className="margin-0 color-gray-800 text-xl-bold text-truncate w-100">{p.name}</p>
                                    <div className="d-flex flex-row gap-1 align-items-start w-100">
                                        <CustomSVG className={`circle-stroke-gray-700 stroke-gray-700 flex-shrink-0`} SvgFunction={MapPin} height={18} width={18} />
                                        <p className="margin-0 color-gray-700 text-sm-regular text-truncate">{(p.address && p.address.city) ? p.address.city : "-"}</p>
                                    </div>
                                </div>
                                {p.short_description && <div className={`${classes["provider-list-card-description"]} text-sm-regular color-gray-500`} dangerouslySetInnerHTML={{__html: p.short_description}} />}
                                <div className="d-flex flex-row gap-3 flex-wrap align-items-center">
                                    <div>
                                        <CustomButton size="lg" hierarchy="primary" >Kapcsolatfelvétel</CustomButton>
                                    </div>
                                    {/*<div>
                                        <CustomButton size="lg" hierarchy="link-gray" onClick={() => authCtx.addToFavourite(p.id)} >{authCtx.tempFavourites.includes(p.id) ? "Eltávolítom a kedvencekből" : "Hozzáadom a kedvencekhez"}</CustomButton>
                                    </div>*/}
                                </div>
                            </div>
                        </Link>
                        <div  className={`${classes["button-container-on-image"]}`}>
                            <LikeButton providerId={p.id} initialActive={Boolean(p.is_favourite)} size={"sm"} />
                            {p.is_highlight && <CustomBadge size="md" color={`primary`} >Kiemelt</CustomBadge>}
                        </div>
                    </div>)}
                    <div className={`${classes["public-providers-grid-container"]} ${providerInfos.viewType === "grid" ? classes["active"] : ""}`}>
                        {filters.providers.providers.map((p, index) => <div key={`public-provider-grid-card-${p.id}`} className={`${classes["public-providers-grid-card"]}`}>
                            <Link className={`w-100 h-100 d-flex flex-column pointer text-decoration-none`} title={p.name} href={`/${p.url}`} rel="noopener noreferrer" target="_blank">
                                <div className={`${classes["provider-grid-card-img-container"]} ${(p.images && p.images.length > 0) ? classes["non-empty"] : ""}`}>
                                    {p.images && p.images.length > 0
                                        ? <img
                                            src={customImageLoader({ src: p.images[0], width: 640, quality: 75 })}
                                            srcSet={`${customImageLoader({ src: p.images[0], width: 640, quality: 75 })} 640w, ${customImageLoader({ src: p.images[0], width: 1080, quality: 75 })} 1080w`}
                                                                                        sizes={PUBLIC_PROVIDER_CARD_SIZES}
                                            alt={p.name}
                                            loading={index === 0 ? 'eager' : 'lazy'}
                                            fetchPriority={index === 0 ? 'high' : undefined}
                                            decoding="async"
                                            style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', inset: 0 }}
                                          />
                                                                                : <SafeImage sizes={PUBLIC_PROVIDER_CARD_SIZES} src={EmptyProviderLogo} alt={"Szolgáltató képe"} />
                                    }
                                </div>
                                <div className={`${classes["provider-list-card-text-container"]}  overflow-hidden`}>
                                    <div className="d-flex flex-column align-self-stretch w-100 text-truncate gap-1 flex-shrink-0">
                                        <p className="margin-0 color-gray-800 text-xl-bold text-truncate w-100">{p.name}</p>
                                        <div className="d-flex flex-row gap-1 align-items-start w-100">
                                            <CustomSVG className={`circle-stroke-gray-700 stroke-gray-700 flex-shrink-0`} SvgFunction={MapPin} height={18} width={18} />
                                            <p className="margin-0 color-gray-700 text-sm-regular text-truncate">{(p.address && p.address.city) ? p.address.city : "-"}</p>
                                        </div>
                                    </div>
                                    <ExtendableDescription description={p.short_description} />
                                    <div className="d-flex flex-column gap-3 align-items-center align-self-stretch">
                                        <div className={`${classes["contact-button-container"]} w-100`}>
                                            <CustomButton className="w-100" size="lg" hierarchy="primary" >Kapcsolatfelvétel</CustomButton>
                                        </div>
                                        {/*<CustomButton className="w-100" size="lg" hierarchy="link-gray" onClick={() => authCtx.addToFavourite(p.id)} >{authCtx.tempFavourites.includes(p.id) ? "Eltávolítom a kedvencekből" : "Hozzáadom a kedvencekhez"}</CustomButton>*/}
                                    </div>
                                </div>
                            </Link>
                            <div  className={`${classes["button-container-on-image"]}`}>
                                <LikeButton providerId={p.id} initialActive={Boolean(p.is_favourite)} size={"sm"} />
                                {p.is_highlight && <CustomBadge size="md" color={`primary`} >Kiemelt</CustomBadge>}
                            </div>
                        </div>)}
                    </div>
                </>
            )
        }
        {/*filters.pager.page !== filters.pager.page_nums && filters.pager.page_nums > 0 && <div className="d-flex flex-column align-self-stretch align-items-center gap-2">
            <div className="d-flex flex-row color-gray-800">
                {filters.distance
                    ? <>
                        <p className="margin-0 text-md-semibold">{`${filters.distance} km`}</p>
                        <p className="margin-0 text-md-regular">{`-en belül található még`}</p>
                        <p className="margin-0 text-md-semibold padding-x-1">{`${(others_count >= 0 ? others_count : 0)} db`}</p>
                        <p className="margin-0 text-md-regular">{`találat`}</p>
                    </>
                    : <>
                        <p className="margin-0 text-md-semibold padding-right-1">{`${(others_count >= 0 ? others_count : 0)} db`}</p>
                        <p className="margin-0 text-md-regular">{`további találat`}</p>
                    </>
                }
            </div>
            <CustomButton size="lg" hierarchy="secondary-gray" onClick={showMore} >Továbbiak megjelenítése </CustomButton>
        </div>*/}
        {(!!anotherCount && filters.providers && filters.providers.providers && filters.providers.providers.length !==0) && filters.distance < 25 && (filters.pager.page_nums === 0 || filters.pager.page === filters.pager.page_nums) && <CustomButton size="lg" hierarchy="primary" onClick={() => loadMore(25)} >{`25 km-en belül van még ${anotherCount - filters.pager.count_rows} db. találat`}</CustomButton>}
        {(!!anotherCount && filters.providers && filters.providers.providers && filters.providers.providers.length !==0) && filters.distance < 50 && filters.distance >= 25 && (filters.pager.page_nums === 0 || filters.pager.page === filters.pager.page_nums) && <CustomButton size="lg" hierarchy="primary" onClick={() => loadMore(50)} >{`50 km-en belül van még ${anotherCount - filters.pager.count_rows} db. találat`}</CustomButton>}
    </div>
}

const CustomToggle = forwardRef(function CustomToggle({ children, onClick, active, title }, ref) {
    return (
        <div ref={ref} onClick={(e) => {e.preventDefault(); onClick(e);}} className="d-flex flex-column">
            <div className="d-flex text-sm-medium color-gray-700 text-nowrap gap-1">
                {title}
            </div>
            <DropdownBase className="pointer text-nowrap d-flex justify-content-between align-items-start gap-2 w-100" active={active} >
                {children}
                <CustomSVG className="stroke-gray-500 flex-shrink-0" SvgFunction={active ? ChevronUp : ChevronDown} height={20} width={20} />
            </DropdownBase>
        </div>
    )
});

function PublicProviderHeaderDropdown({label, type, options, value, handleSelect}) {
    const [isOpen, setIsOpen] = useState(false);
    
    const toggleIsOpen = () => { setIsOpen(!isOpen); };

    let temp_options = [...options];

    let options_index = temp_options.findIndex(o => o.value === value);
    if(options_index === -1) options_index = 0;

    return <Dropdown show={isOpen} onToggle={toggleIsOpen} style={{width: 200}} >
        <Dropdown.Toggle as={CustomToggle} active={isOpen} title={label}>
            {temp_options[options_index].name}
        </Dropdown.Toggle>
        <Dropdown.Menu style={{width: 300}}>
            {temp_options.map(o => <Dropdown.Item key={`sort-option-${type}-${o.value}`} className="d-flex justify-content-between" onClick={(e) => handleSelect(e, o.value, type)} active={o.value === value}>
                {o.name}
                {o.value === value && <CustomSVG className="stroke-gray-900" SvgFunction={Check} height={20} width={20} />}
            </Dropdown.Item>)}
        </Dropdown.Menu>
    </Dropdown>
}

function NoResultDiv({clickEvent, filters}) {
    return <div className={`${classes["public-provider-list-no-result-container"]}`}>
        <CustomSVG className="flex-shrink-0 stroke-warning-500 circle-stroke-warning-500" SvgFunction={AlertCircle} height={24} width={24} />
        <div className="d-flex flex-grow-1 flex-column align-self-stretch align-items-start gap-3">
            <div className="d-flex w-100 flex-column align-self-stretch align-items-start gap-1">
                <p className="margin-0 color-gray-800 text-sm-semibold">Nincs találat</p>
                <p className="margin-0 color-gray-600 text-sm-regular">A kiválasztott feltételeknek megfelelően nincs találat, módosíts a feltételeken vagy keress a környéken.</p>
            </div>
            {((!filters.distance || filters.distance < 50) && true) && <CustomButton size="md" hierarchy="link-gray" onClick={() => clickEvent(filters.distance >= 25 ? 50 : 25)} icon="trailing" is_svg={true} svg={ArrowRight} >Keresés a környéken</CustomButton>}
        </div>
    </div>
}

function ExtendableDescription({description}) {
    const [isExtended, setIsExtended] = useState(false);

    const toggleExtending = (e) => {  setIsExtended(!isExtended); e.preventDefault(); e.stopPropagation(); };

    if(!description) return null;

    return <>
        <div className={`${classes["provider-grid-card-description"]} ${isExtended ? classes["extended"] : ""} text-sm-regular color-gray-500`} dangerouslySetInnerHTML={{__html: description}} />
        <CustomButton size="md" hierarchy="link-gray" onClick={toggleExtending} >{isExtended ? "Kevesebb megjelenítés" : "Tovább olvasom"}</CustomButton>
    </>
}